'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Tabs,
  Tab,
  Spinner,
  Alert,
} from 'react-bootstrap';
import {
  Send,
  Bot,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  Wrench,
  User,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { ConfigReader } from '../../config/ConfigReader';
import {
  CsAgentClient,
  type ConversationMessage,
  type ToolCallLog,
  type SendMessageRequest,
} from '../../infrastructure/CsAgentClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  id: string;
  role: 'user' | 'agent' | 'human_agent' | 'system';
  body: string;
  source: string;
  createdAt: string;
  responseTimeMs?: number;
}

interface PendingAction {
  actionId: string;
  type: string;
  description: string;
  conversationId: string;
  [key: string]: unknown;
}

interface ToolCallEntry extends ToolCallLog {
  id: string;
  status: 'success' | 'error';
}

interface PlaygroundConfig {
  customerId: string;
  ticketId: string;
  email: string;
  channel: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateNumericId(): string {
  return `${Math.floor(Math.random() * 900000000) + 100000000}`;
}

function generateTicketId(): string {
  return generateNumericId();
}

function generateEmail(): string {
  return `test-${generateNumericId().slice(0, 6)}@skinseoul-test.com`;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CollapsibleJson({ label, data }: { label: string; data: unknown }) {
  const [open, setOpen] = useState(false);
  const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);

  return (
    <div className="mb-1">
      <button
        className="btn btn-sm btn-link p-0 text-decoration-none d-flex align-items-center gap-1"
        onClick={() => setOpen(!open)}
        type="button"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
          {label}
        </span>
      </button>
      {open && (
        <pre
          className="bg-dark text-light p-2 rounded mt-1 mb-0"
          style={{
            fontSize: '0.7rem',
            maxHeight: 200,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {text}
        </pre>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export default function CsPlaygroundPage() {
  // --- Config state ---
  const [config, setConfig] = useState<PlaygroundConfig>({
    customerId: generateNumericId(),
    ticketId: generateTicketId(),
    email: generateEmail(),
    channel: 'chat',
  });

  // --- Chat state ---
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'system-init',
      role: 'system',
      body: 'Conversation started - Test Mode',
      source: 'system',
      createdAt: new Date().toISOString(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Inspector state ---
  const [toolCalls, setToolCalls] = useState<ToolCallEntry[]>([]);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [conversationContext, setConversationContext] = useState<Record<string, unknown> | null>(
    null,
  );
  const [inspectorTab, setInspectorTab] = useState<string>('toolCalls');

  // --- Rating state ---
  const [ratings, setRatings] = useState<Map<number, number>>(new Map());

  // --- Refs ---
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const clientRef = useRef<CsAgentClient | null>(null);

  // Lazy-init the client
  const getClient = useCallback(() => {
    if (!clientRef.current) {
      const apiUrl = ConfigReader.getInstance().getConfig().csAgentApiUrl;
      clientRef.current = new CsAgentClient(apiUrl);
    }
    return clientRef.current;
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Re-focus input when loading completes
  useEffect(() => {
    if (!isLoading) {
      inputRef.current?.focus();
    }
  }, [isLoading]);

  // ------------------------------------------------------------------
  // Send message handler
  // ------------------------------------------------------------------

  const handleSend = useCallback(
    async (fromAgent: boolean) => {
      const text = inputText.trim();
      if (!text || isLoading) return;

      setError(null);
      setInputText('');

      // Add user/agent message to local state immediately
      const localMsg: ChatMessage = {
        id: `local-${Date.now()}`,
        role: fromAgent ? 'human_agent' : 'user',
        body: text,
        source: fromAgent ? 'agent' : 'customer',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, localMsg]);
      setIsLoading(true);

      const startTime = performance.now();

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30_000);

      try {
        const client = getClient();

        const request: SendMessageRequest = {
          gorgiasCustomerId: config.customerId,
          gorgiasTicketId: config.ticketId,
          message: text,
          fromAgent,
          channel: config.channel,
          email: config.email,
        };

        const response = await client.sendMessage(request, controller.signal);
        clearTimeout(timeout);

        const elapsed = Math.round(performance.now() - startTime);

        // Build messages from response
        const serverMessages: ChatMessage[] = response.messages.map(
          (m: ConversationMessage, i: number) => ({
            id: `srv-${Date.now()}-${i}`,
            role: m.role === 'user' ? ('user' as const) : m.role === 'human_agent' ? ('human_agent' as const) : ('agent' as const),
            body: m.body,
            source: m.source,
            createdAt: m.createdAt,
          }),
        );

        // Find the last agent message and attach response time
        const lastAgentIdx = serverMessages.map((m) => m.role).lastIndexOf('agent');
        if (lastAgentIdx >= 0) {
          serverMessages[lastAgentIdx]!.responseTimeMs = elapsed;
        }

        setMessages([
          {
            id: 'system-init',
            role: 'system',
            body: 'Conversation started - Test Mode',
            source: 'system',
            createdAt: new Date().toISOString(),
          },
          ...serverMessages,
        ]);

        // Update tool calls from response
        if (response.toolCalls) {
          const newToolCalls: ToolCallEntry[] = response.toolCalls.map((tc, i) => ({
            ...tc,
            id: `tc-${Date.now()}-${i}`,
            status: 'success' as const,
          }));
          setToolCalls((prev) => [...prev, ...newToolCalls]);
        }

        // Fetch conversation for pending actions and context
        try {
          const convResp = await client.getConversation(config.ticketId);
          if (convResp.found) {
            if (convResp.pendingActions) {
              setPendingActions(
                convResp.pendingActions.map((a, i) => ({
                  actionId: (a['actionId'] as string) || `action-${i}`,
                  type: (a['type'] as string) || 'unknown',
                  description: (a['description'] as string) || JSON.stringify(a),
                  conversationId: response.conversationId || config.ticketId,
                  ...a,
                })),
              );
            }
            if (convResp.conversation) {
              setConversationContext(convResp.conversation);
            }
          }
        } catch {
          // Non-critical: don't block chat for this
        }
      } catch (err: unknown) {
        clearTimeout(timeout);
        if (err instanceof DOMException && err.name === 'AbortError') {
          setError('Request timed out after 30 seconds. Please try again.');
        } else {
          setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [inputText, isLoading, config, getClient],
  );

  // ------------------------------------------------------------------
  // Resolve action handler
  // ------------------------------------------------------------------

  const handleResolveAction = useCallback(
    async (action: PendingAction, approved: boolean) => {
      try {
        const client = getClient();
        await client.resolveAction(action.actionId, {
          conversationId: action.conversationId,
          approved,
          reason: approved ? 'Approved via playground' : 'Rejected via playground',
        });
        setPendingActions((prev) => prev.filter((a) => a.actionId !== action.actionId));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to resolve action.');
      }
    },
    [getClient],
  );

  // ------------------------------------------------------------------
  // Reset handler
  // ------------------------------------------------------------------

  const handleReset = useCallback(() => {
    const newTicketId = generateTicketId();
    setConfig((prev) => ({ ...prev, ticketId: newTicketId }));
    setMessages([
      {
        id: 'system-init',
        role: 'system',
        body: 'Conversation started - Test Mode',
        source: 'system',
        createdAt: new Date().toISOString(),
      },
    ]);
    setToolCalls([]);
    setPendingActions([]);
    setConversationContext(null);
    setError(null);
    setInputText('');
    setRatings(new Map());
  }, []);

  // ------------------------------------------------------------------
  // Rating handler
  // ------------------------------------------------------------------

  const handleRate = useCallback(
    async (messageIndex: number, rating: number) => {
      try {
        const client = getClient();
        await client.saveRating({
          conversationId: config.ticketId,
          messageIndex,
          rating,
        });
        setRatings((prev) => {
          const next = new Map(prev);
          next.set(messageIndex, rating);
          return next;
        });
      } catch (err) {
        console.error('Failed to save rating:', err);
      }
    },
    [config.ticketId, getClient],
  );

  // ------------------------------------------------------------------
  // Key handler for Enter
  // ------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend(false);
      }
    },
    [handleSend],
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <Container fluid className="py-3 px-3" style={{ height: 'calc(100vh - 56px)' }}>
      {/* Page Header */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <Bot size={24} />
        <h4 className="mb-0">CS Agent Playground</h4>
        <Badge bg="warning" text="dark" className="ms-2">
          Test Mode
        </Badge>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          {error}
        </Alert>
      )}

      <Row className="g-3" style={{ height: 'calc(100% - 60px)', overflow: 'hidden' }}>
        {/* ============================================================ */}
        {/* LEFT COLUMN - Config Panel                                   */}
        {/* ============================================================ */}
        <Col style={{ flex: '0 0 280px', maxWidth: 280 }}>
          <Card className="h-100">
            <Card.Header className="fw-semibold d-flex align-items-center gap-2">
              <Wrench size={16} />
              Test Configuration
            </Card.Header>
            <Card.Body className="d-flex flex-column gap-3">
              <Form.Group controlId="cs-playground-customer-id">
                <Form.Label className="small fw-medium mb-1">Gorgias Customer ID</Form.Label>
                <Form.Control
                  size="sm"
                  value={config.customerId}
                  onChange={(e) => setConfig((p) => ({ ...p, customerId: e.target.value }))}
                />
              </Form.Group>

              <Form.Group controlId="cs-playground-ticket-id">
                <Form.Label className="small fw-medium mb-1">Gorgias Ticket ID</Form.Label>
                <Form.Control
                  size="sm"
                  value={config.ticketId}
                  onChange={(e) => setConfig((p) => ({ ...p, ticketId: e.target.value }))}
                />
              </Form.Group>

              <Form.Group controlId="cs-playground-channel">
                <Form.Label className="small fw-medium mb-1">Channel</Form.Label>
                <Form.Select
                  size="sm"
                  value={config.channel}
                  onChange={(e) => setConfig((p) => ({ ...p, channel: e.target.value }))}
                >
                  <option value="chat">Chat</option>
                  <option value="email">Email</option>
                  <option value="instagram">Instagram</option>
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="cs-playground-customer-email">
                <Form.Label className="small fw-medium mb-1">Customer Email</Form.Label>
                <Form.Control
                  size="sm"
                  type="email"
                  value={config.email}
                  onChange={(e) => setConfig((p) => ({ ...p, email: e.target.value }))}
                />
              </Form.Group>

              <hr className="my-1" />

              <Button variant="outline-secondary" size="sm" onClick={handleReset} className="w-100">
                <RotateCcw size={14} className="me-1" />
                Reset Conversation
              </Button>

            </Card.Body>
          </Card>
        </Col>

        {/* ============================================================ */}
        {/* CENTER COLUMN - Chat Area                                    */}
        {/* ============================================================ */}
        <Col className="h-100" style={{ minHeight: 0 }}>
          <Card className="h-100 d-flex flex-column" style={{ maxHeight: '100%' }}>
            {/* Chat Header */}
            <Card.Header className="d-flex align-items-center justify-content-between py-2">
              <div className="d-flex align-items-center gap-2 small">
                <MessageSquare size={14} />
                <span className="fw-medium text-truncate" style={{ maxWidth: 200 }}>
                  {config.ticketId}
                </span>
                <Badge bg="secondary" className="text-uppercase" style={{ fontSize: '0.65rem' }}>
                  {config.channel}
                </Badge>
              </div>
              <div className="d-flex align-items-center gap-2 small text-muted">
                <User size={12} />
                <span>{config.email}</span>
                <Badge bg={isLoading ? 'warning' : 'success'} style={{ fontSize: '0.65rem' }}>
                  {isLoading ? 'Processing' : 'Ready'}
                </Badge>
              </div>
            </Card.Header>

            {/* Messages Area */}
            <div
              className="flex-grow-1 p-3"
              style={{ overflowY: 'auto', minHeight: 0, background: '#f8f9fa' }}
            >
              {messages.map((msg, msgIndex) => {
                // System message
                if (msg.role === 'system') {
                  return (
                    <div key={msg.id} className="text-center mb-3">
                      <Badge bg="warning" text="dark" style={{ fontSize: '0.75rem' }}>
                        {msg.body}
                      </Badge>
                    </div>
                  );
                }

                const isCustomer = msg.role === 'user';
                const isHumanAgent = msg.role === 'human_agent';
                const isRightAligned = isCustomer || isHumanAgent;
                const currentRating = ratings.get(msgIndex);

                return (
                  <div
                    key={msg.id}
                    className={`d-flex mb-3 ${isRightAligned ? 'justify-content-end' : 'justify-content-start'}`}
                  >
                    <div style={{ maxWidth: '75%' }}>
                      <div
                        style={{
                          padding: '8px 12px',
                          borderRadius: isRightAligned ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                          background: isCustomer ? '#0d6efd' : isHumanAgent ? '#198754' : '#e9ecef',
                          color: isRightAligned ? '#fff' : '#212529',
                          fontSize: '0.875rem',
                          lineHeight: 1.5,
                          wordBreak: 'break-word',
                        }}
                      >
                          <div
                          className="d-flex align-items-center gap-1 mb-1"
                          style={{ fontSize: '0.7rem', opacity: 0.7 }}
                        >
                          {isCustomer ? (
                            <>
                              <User size={10} />
                              <span className="fw-medium">Customer</span>
                            </>
                          ) : isHumanAgent ? (
                            <>
                              <User size={10} />
                              <span className="fw-medium">Human Agent</span>
                            </>
                          ) : (
                            <>
                              <Bot size={10} />
                              <span className="fw-medium">CS Agent</span>
                            </>
                          )}
                        </div>
                        <div className="cs-markdown" style={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                          {isRightAligned ? msg.body : <ReactMarkdown>{msg.body}</ReactMarkdown>}
                        </div>
                        <div
                          className="d-flex align-items-center justify-content-end gap-2 mt-1"
                          style={{ fontSize: '0.65rem', opacity: 0.6 }}
                        >
                          <span>{formatTime(msg.createdAt)}</span>
                          {msg.responseTimeMs !== undefined && (
                            <span className="d-flex align-items-center gap-1">
                              <Clock size={9} />
                              {(msg.responseTimeMs / 1000).toFixed(1)}s
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Rating buttons for agent messages */}
                      {!isRightAligned && (
                        <div className="d-flex justify-content-end gap-1 mt-1">
                          <button
                            type="button"
                            className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              background: currentRating === 1 ? '#d1fae5' : 'transparent',
                              color: currentRating === 1 ? '#059669' : '#9ca3af',
                              transition: 'all 0.15s',
                            }}
                            title="Good response"
                            onClick={() => handleRate(msgIndex, 1)}
                          >
                            <ThumbsUp size={12} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              background: currentRating === -1 ? '#fee2e2' : 'transparent',
                              color: currentRating === -1 ? '#dc2626' : '#9ca3af',
                              transition: 'all 0.15s',
                            }}
                            title="Bad response"
                            onClick={() => handleRate(msgIndex, -1)}
                          >
                            <ThumbsDown size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Thinking indicator */}
              {isLoading && (
                <div className="d-flex justify-content-start mb-3">
                  <div
                    style={{
                      padding: '8px 16px',
                      borderRadius: '12px 12px 12px 2px',
                      background: '#e9ecef',
                      fontSize: '0.85rem',
                    }}
                    className="d-flex align-items-center gap-2"
                  >
                    <Spinner animation="border" size="sm" variant="secondary" />
                    <span className="text-muted">Agent is thinking...</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <Card.Footer className="py-2 px-3">
              <div className="d-flex gap-2">
                <Form.Control
                  ref={inputRef}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  disabled={isLoading}
                  size="sm"
                  autoFocus
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSend(false)}
                  disabled={isLoading || !inputText.trim()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {isLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <Send size={14} className="me-1" />
                      Send
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleSend(true)}
                  disabled={isLoading || !inputText.trim()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <Bot size={14} className="me-1" />
                  Agent
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>

        {/* ============================================================ */}
        {/* RIGHT COLUMN - Inspector Panel                               */}
        {/* ============================================================ */}
        <Col style={{ flex: '0 0 320px', maxWidth: 320 }}>
          <Card className="h-100 d-flex flex-column">
            <Card.Body className="p-0 d-flex flex-column" style={{ minHeight: 0 }}>
              <Tabs
                activeKey={inspectorTab}
                onSelect={(k) => setInspectorTab(k || 'toolCalls')}
                className="px-2 pt-2"
                variant="underline"
              >
                <Tab eventKey="toolCalls" title="Tool Calls">
                  <div className="p-2" style={{ overflowY: 'auto', flex: 1 }}>
                    {toolCalls.length === 0 ? (
                      <div className="text-center text-muted py-4 small">
                        <Wrench size={24} className="mb-2 opacity-50" />
                        <div>No tool calls yet</div>
                        <div style={{ fontSize: '0.75rem' }}>
                          Send a message to see agent tool usage
                        </div>
                      </div>
                    ) : (
                      toolCalls.map((tc) => (
                        <Card key={tc.id} className="mb-2" style={{ fontSize: '0.8rem' }}>
                          <Card.Body className="py-2 px-3">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <span className="fw-medium" style={{ color: '#7c3aed' }}>
                                {tc.name}
                              </span>
                              <Badge
                                bg={tc.status === 'success' ? 'success' : 'danger'}
                                style={{ fontSize: '0.65rem' }}
                              >
                                {tc.status}
                              </Badge>
                            </div>
                            <CollapsibleJson label="Arguments" data={tc.args} />
                            <CollapsibleJson label="Result" data={tc.result} />
                            <div
                              className="text-muted d-flex align-items-center gap-1 mt-1"
                              style={{ fontSize: '0.7rem' }}
                            >
                              <Clock size={10} />
                              {tc.durationMs}ms
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    )}
                  </div>
                </Tab>

                <Tab eventKey="actions" title="Actions">
                  <div className="p-2" style={{ overflowY: 'auto', flex: 1 }}>
                    {pendingActions.length === 0 ? (
                      <div className="text-center text-muted py-4 small">
                        <CheckCircle size={24} className="mb-2 opacity-50" />
                        <div>No pending actions</div>
                        <div style={{ fontSize: '0.75rem' }}>
                          Actions requiring approval will appear here
                        </div>
                      </div>
                    ) : (
                      pendingActions.map((action) => (
                        <Card key={action.actionId} className="mb-2" style={{ fontSize: '0.8rem' }}>
                          <Card.Body className="py-2 px-3">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <Badge bg="info" style={{ fontSize: '0.65rem' }}>
                                {action.type}
                              </Badge>
                            </div>
                            <p className="mb-2 small">{action.description}</p>
                            <div className="d-flex gap-2">
                              <Button
                                variant="success"
                                size="sm"
                                style={{ fontSize: '0.75rem' }}
                                onClick={() => handleResolveAction(action, true)}
                              >
                                <CheckCircle size={12} className="me-1" />
                                Approve
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                style={{ fontSize: '0.75rem' }}
                                onClick={() => handleResolveAction(action, false)}
                              >
                                <XCircle size={12} className="me-1" />
                                Reject
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    )}
                  </div>
                </Tab>

                <Tab eventKey="context" title="Context">
                  <div className="p-2" style={{ overflowY: 'auto', flex: 1 }}>
                    {conversationContext ? (
                      <>
                        {conversationContext['summary'] && (
                          <Card className="mb-2" style={{ fontSize: '0.8rem' }}>
                            <Card.Header
                              className="py-1 px-3 fw-medium"
                              style={{ fontSize: '0.75rem' }}
                            >
                              Conversation Summary
                            </Card.Header>
                            <Card.Body className="py-2 px-3">
                              <p className="mb-0 small">
                                {String(conversationContext['summary'])}
                              </p>
                            </Card.Body>
                          </Card>
                        )}
                        <Card style={{ fontSize: '0.8rem' }}>
                          <Card.Header
                            className="py-1 px-3 fw-medium"
                            style={{ fontSize: '0.75rem' }}
                          >
                            Conversation Data
                          </Card.Header>
                          <Card.Body className="py-2 px-3">
                            <pre
                              className="mb-0"
                              style={{
                                fontSize: '0.7rem',
                                maxHeight: 300,
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                              }}
                            >
                              {JSON.stringify(conversationContext, null, 2)}
                            </pre>
                          </Card.Body>
                        </Card>
                      </>
                    ) : (
                      <div className="text-center text-muted py-4 small">
                        <User size={24} className="mb-2 opacity-50" />
                        <div>No context loaded</div>
                        <div style={{ fontSize: '0.75rem' }}>
                          Context will load after first message exchange
                        </div>
                      </div>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
